import { randomId } from "@codeshare/protocol";
import * as Y from "yjs";

/** Layout version of the shared document (docs/PRD.md §9). Bump only with a migration. */
export const SCHEMA_VERSION = 1;

/** Prefix of the meta keys holding the room's toolchain choice per runner. */
const TOOLCHAIN = "toolchain.";

/** How a file's language was decided; "user" overrides beat detection on every peer. */
export interface FileLanguage {
  id: string;
  source: string;
}

export interface FileEntry {
  id: string;
  path: string;
  text: Y.Text;
  lang?: FileLanguage;
  createdBy?: string;
}

/** Thrown when this build must not modify a document written by a newer schema. */
export class ReadOnlyError extends Error {
  constructor(schemaVersion: number) {
    super(
      `document schema v${schemaVersion} is newer than this build (v${SCHEMA_VERSION}); update to edit`,
    );
    this.name = "ReadOnlyError";
  }
}

/**
 * Upgrades a document from version `n - 1` to `n`. Migrations must be additive (new keys), so
 * older peers can still read what they understand.
 */
export type Migration = (doc: Y.Doc) => void;

export const MIGRATIONS: Record<number, Migration> = {
  1: () => {}, // initial layout
};

/**
 * Typed access to the shared workspace:
 *
 *   meta:  Y.Map   { schemaVersion, "toolchain.<runner>"? }
 *   files: Y.Map<fileId, Y.Map { path, text: Y.Text, lang?, createdBy }>
 *   order: Y.Array<fileId>   (tab order)
 *   sessions: Y.Map          (run sessions; used by terminal streaming)
 */
export class Workspace {
  readonly doc: Y.Doc;
  readonly meta: Y.Map<unknown>;
  readonly files: Y.Map<Y.Map<unknown>>;
  readonly order: Y.Array<string>;
  readonly sessions: Y.Map<unknown>;
  readonly #migrations: Record<number, Migration>;
  readonly #version: number;

  constructor(
    doc: Y.Doc = new Y.Doc(),
    options: { migrations?: Record<number, Migration>; version?: number } = {},
  ) {
    this.doc = doc;
    this.meta = doc.getMap("meta");
    this.files = doc.getMap("files");
    this.order = doc.getArray("order");
    this.sessions = doc.getMap("sessions");
    this.#migrations = options.migrations ?? MIGRATIONS;
    this.#version = options.version ?? SCHEMA_VERSION;
  }

  /** Schema the document was written with (0 = empty/unknown). */
  get schemaVersion(): number {
    const v = this.meta.get("schemaVersion");
    return typeof v === "number" ? v : 0;
  }

  /** False when a newer build wrote the document: we may not understand it, so don't touch it. */
  get canWrite(): boolean {
    return this.schemaVersion <= this.#version;
  }

  /** Brings the document up to this build's schema. Safe to call repeatedly and concurrently. */
  migrate(): void {
    if (!this.canWrite) return;
    const from = this.schemaVersion;
    if (from === this.#version) return;
    this.doc.transact(() => {
      for (let v = from + 1; v <= this.#version; v++) this.#migrations[v]?.(this.doc);
      this.meta.set("schemaVersion", this.#version);
    });
  }

  /** Files in tab order (files missing from `order`, e.g. after a concurrent edit, go last). */
  list(): FileEntry[] {
    const ids = new Set<string>();
    const entries: FileEntry[] = [];
    for (const id of [...this.order.toArray(), ...this.files.keys()]) {
      if (ids.has(id)) continue;
      ids.add(id);
      const entry = this.get(id);
      if (entry) entries.push(entry);
    }
    return entries;
  }

  get(id: string): FileEntry | undefined {
    const map = this.files.get(id);
    if (!map) return undefined;
    const path = map.get("path");
    const text = map.get("text");
    if (typeof path !== "string" || !(text instanceof Y.Text)) return undefined;
    const entry: FileEntry = { id, path, text };
    const lang = map.get("lang") as FileLanguage | undefined;
    if (lang && typeof lang.id === "string") entry.lang = lang;
    const createdBy = map.get("createdBy");
    if (typeof createdBy === "string") entry.createdBy = createdBy;
    return entry;
  }

  findByPath(path: string): FileEntry | undefined {
    return this.list().find((f) => f.path === path);
  }

  create(options: {
    path: string;
    content?: string;
    createdBy?: string;
    lang?: FileLanguage;
  }): string {
    this.#assertWritable();
    if (this.findByPath(options.path))
      throw new Error(`a file named ${options.path} already exists`);
    const id = randomId();
    this.doc.transact(() => {
      const map = new Y.Map<unknown>();
      const text = new Y.Text(options.content ?? "");
      map.set("path", options.path);
      map.set("text", text);
      if (options.createdBy) map.set("createdBy", options.createdBy);
      if (options.lang) map.set("lang", options.lang);
      this.files.set(id, map);
      this.order.push([id]);
    });
    return id;
  }

  rename(id: string, path: string): void {
    this.#assertWritable();
    const existing = this.findByPath(path);
    if (existing && existing.id !== id) throw new Error(`a file named ${path} already exists`);
    this.#map(id).set("path", path);
  }

  delete(id: string): void {
    this.#assertWritable();
    this.doc.transact(() => {
      this.files.delete(id);
      const index = this.order.toArray().indexOf(id);
      if (index >= 0) this.order.delete(index, 1);
    });
  }

  /** The room's choice of toolchain for a runner (e.g. "oxcaml" for "ocaml"), if any. */
  toolchain(runnerId: string): string | undefined {
    const value = this.meta.get(`${TOOLCHAIN}${runnerId}`);
    return typeof value === "string" ? value : undefined;
  }

  /** Every runner's toolchain choice, by runner. Values come from peers: callers validate them. */
  toolchains(): Record<string, string> {
    const choices: Record<string, string> = {};
    for (const [key, value] of this.meta.entries()) {
      if (key.startsWith(TOOLCHAIN) && typeof value === "string")
        choices[key.slice(TOOLCHAIN.length)] = value;
    }
    return choices;
  }

  setToolchain(runnerId: string, toolchain: string | undefined): void {
    this.#assertWritable();
    if (toolchain) this.meta.set(`${TOOLCHAIN}${runnerId}`, toolchain);
    else this.meta.delete(`${TOOLCHAIN}${runnerId}`);
  }

  setLanguage(id: string, lang: FileLanguage | undefined): void {
    this.#assertWritable();
    const map = this.#map(id);
    if (lang) map.set("lang", lang);
    else map.delete("lang");
  }

  /** Moves a file to a new tab position. */
  move(id: string, toIndex: number): void {
    this.#assertWritable();
    const ids = this.order.toArray();
    const from = ids.indexOf(id);
    if (from < 0) return;
    this.doc.transact(() => {
      this.order.delete(from, 1);
      this.order.insert(Math.max(0, Math.min(toIndex, this.order.length)), [id]);
    });
  }

  /** Calls `listener` after any change to the file list, names, or languages (not file text). */
  observeFiles(listener: () => void): () => void {
    const onFiles = (events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
      // Text edits inside files don't change the list; skip them.
      if (events.some((e) => !(e.target instanceof Y.Text))) listener();
    };
    this.files.observeDeep(onFiles);
    this.order.observe(listener);
    return () => {
      this.files.unobserveDeep(onFiles);
      this.order.unobserve(listener);
    };
  }

  /** Calls `listener` with the changed keys ("path", "lang", …) whenever one file's fields change. */
  observeFile(id: string, listener: (keys: Set<string>) => void): () => void {
    const map = this.files.get(id);
    if (!map) return () => {};
    const onChange = (event: Y.YMapEvent<unknown>) => listener(event.keysChanged);
    map.observe(onChange);
    return () => map.unobserve(onChange);
  }

  #map(id: string): Y.Map<unknown> {
    const map = this.files.get(id);
    if (!map) throw new Error(`no file ${id}`);
    return map;
  }

  #assertWritable(): void {
    if (!this.canWrite) throw new ReadOnlyError(this.schemaVersion);
  }
}
